import React from "react";
import { Form, Input, Select, DatePicker, Radio, Checkbox, Button, InputNumber } from "antd";
import { mobileNumberValidator } from "../../../../utils/utils";
import AlternateTaxIdentifierField from "../../../../components/onboarding/AlternateTaxIdentifierField.jsx";

const CorporateBeneficiaryForm = ({
  countries,
  countryCodes,
  natureOfBusinessOptions,
  tinReasonOptions,
  hasOtherCrsTaxResident,
  sameAsAbove,
  filterOption,
  handleRegisteredAddressChange,
  handleSameAsAboveChange,
  handleMultiTaxResidentChange,
  isDisabled,
}) => {
  return (
    <>
      {/* Basic Information Section */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Basic Information</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Company Name */}
          <Form.Item
            label="Company Name"
            name="companyName"
            rules={[{ required: true, message: "Please enter company name" }]}
          >
            <Input placeholder="Enter company name" />
          </Form.Item>

          {/* Date Of Incorporation */}
          <Form.Item
            label="Date Of Incorporation"
            name="dateOfIncorporation"
            rules={[
              {
                required: true,
                message: "Please select date of incorporation",
              },
            ]}
          >
            <DatePicker
              className="w-full"
              placeholder="Select date"
              format="DD/MM/YYYY"
            />
          </Form.Item>

          {/* Country of Incorporation */}
          <Form.Item
            label="Country of Incorporation"
            name="countryOfIncorporation"
            rules={[
              {
                required: true,
                message: "Please select country of incorporation",
              },
            ]}
          >
            <Select
              showSearch
              placeholder="Select country"
              filterOption={filterOption}
              options={countries}
            />
          </Form.Item>

          {/* Company Registration Number */}
          <Form.Item
            label="Company Registration Number"
            name="companyRegistrationNumber"
            rules={[
              {
                required: true,
                message: "Please enter company registration number",
              },
            ]}
          >
            <Input placeholder="Enter company registration number" />
          </Form.Item>

          {/* Ownership Percentage */}

          <Form.Item
            label="Ownership Percentage"
            name="ownershipPercentage"
            rules={[
              { required: true, message: "Please enter ownership percentage" },
              {
                type: "number",
                min: 0,
                max: 100,
                message: "Value must be between 0 and 100",
              },
            ]}
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>

          {/* Nature of Business */}
          <Form.Item
            label="Nature of Business"
            name="natureOfBusiness"
            rules={[
              { required: true, message: "Please select nature of business" },
            ]}
          >
            <Select placeholder="Select" options={natureOfBusinessOptions} />
          </Form.Item>

          {/* Brief Description of Business Activities */}
          <Form.Item
            label="Brief Description of Business Activities"
            name="briefDescription"
            rules={[
              { required: true, message: "Please enter brief description" },
            ]}
            className="md:col-span-2"
          >
            <Input.TextArea rows={3} placeholder="Enter brief description" />
          </Form.Item>

          {/* Company's Website */}
          <Form.Item
            label="Company's Website"
            name="companyWebsite"
            rules={[
              { required: true, message: "Please enter company website" },
              { type: "url", message: "Please enter a valid URL" },
            ]}
          >
            <Input placeholder="https://example.com" />
          </Form.Item>

          {/* Company's Email Address */}
          <Form.Item
            label="Company's Email Address"
            name="companyEmail"
            rules={[
              { required: true, message: "Please enter email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input type="email" placeholder="Enter email" />
          </Form.Item>

          {/* Company's Phone Number */}
          <Form.Item label="Company's Phone Number" required>
            <div className="flex w-full gap-2">
              <Form.Item
                name="phoneCountryCode"
                noStyle
                rules={[{ required: true, message: "Country code required" }]}
              >
                <Select
                  showSearch
                  placeholder="Code"
                  filterOption={filterOption}
                  options={countryCodes}
                  style={{ width: "30%" }}
                />
              </Form.Item>
              <Form.Item
                name="phoneNumber"
                noStyle
                dependencies={["phoneCountryCode"]}
                rules={[mobileNumberValidator()]}
              >
                <Input
                  placeholder="Enter mobile number"
                  maxLength={15}
                  inputMode="numeric"
                  onKeyPress={(e) => {
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </div>
          </Form.Item>
          {/* Does the entity have a place of business in the United States? */}
          <Form.Item
            label="Does the entity have a place of business in the United States?"
            name="hasBusinessInUS"
            rules={[{ required: true, message: "Please select an option" }]}
            className="md:col-span-2"
          >
            <Radio.Group>
              <Radio value={true}>Yes</Radio>
              <Radio value={false}>No</Radio>
            </Radio.Group>
          </Form.Item>

          {/* Company Tax Residency */}
          <Form.Item
            label="Company Tax Residency"
            name="companyTaxResidency"
            rules={[
              {
                required: true,
                message: "Please select company tax residency",
              },
            ]}
          >
            <Select
              showSearch
              placeholder="Select country"
              filterOption={filterOption}
              options={countries}
            />
          </Form.Item>

          {/* Legal Entity Identifier */}
          <Form.Item
            label="Legal Entity Identifier"
            name="legalEntityIdentifier"
            rules={[
              {
                required: true,
                message: "Please enter legal entity identifier",
              },
            ]}
          >
            <Input placeholder="Enter legal entity identifier" />
          </Form.Item>
        </div>
      </div>

      {/* Tax Information Section */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Tax Information</h3>

        {/* Common Reporting Standard (CRS) Section */}
        <div className="mt-6">
          <p className="text-sm text-gray-600 !font-normal mb-4">
            For Common Reporting Standard (CRS) purposes, please mention which
            countries you are tax resident of? *
          </p>
          <div className="border border-gray-300 rounded-lg p-6 mt-6">
            <div className="mb-6">
              <Form.Item
                label="Are there any other countries you are tax resident of?"
                name="isMultiTaxDomicileEnabled"
                rules={[{ required: true, message: "Please select an option" }]}
              >
                <Radio.Group onChange={handleMultiTaxResidentChange}>
                  <Radio value={true}>Yes</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item>
            </div>

            <Form.List name="taxDomiciles">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <div
                      key={field.key}
                      className="border border-gray-300 rounded-lg p-6 mt-6"
                    >
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold">
                          {index === 0
                            ? "Tax Resident Information"
                            : `Additional Tax Resident Information ${index}`}
                        </h3>

                        {index !== 0 && (
                          <Button
                            type="link"
                            danger
                            onClick={() => remove(field.name)}
                          >
                            Remove
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                          label="Select Country"
                          name={[field.name, "taxDomicileCountryId"]}
                          rules={[
                            {
                              required: true,
                              message: "Please select country",
                            },
                          ]}
                        >
                          <Select
                            placeholder="Select country"
                            options={countries}
                          />
                        </Form.Item>

                        <Form.Item shouldUpdate>
                          {({ getFieldValue }) => {
                            const noTIN = getFieldValue([
                              "taxDomiciles",
                              index,
                              "isTINIDAvailable",
                            ]);

                            return (
                              <Form.Item
                                label="Tax Identification Number"
                                name={[field.name, "taxIDNumber"]}
                                rules={
                                  noTIN
                                    ? []
                                    : [
                                        {
                                          required: true,
                                          message: "Enter TIN",
                                        },
                                      ]
                                }
                                className="w-full"
                                disabled={noTIN || isDisabled}
                              >
                                <Input
                                  placeholder="Enter tax identification number"
                                  disabled={noTIN || isDisabled}
                                />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>

                        <Form.Item
                          name={[field.name, "isTINIDAvailable"]}
                          valuePropName="checked"
                          className="md:col-span-2"
                        >
                          <Checkbox>Check if you don't have TIN?</Checkbox>
                        </Form.Item>

                        <Form.Item className="md:col-span-2" shouldUpdate>
                          {({ getFieldValue }) => {
                            const noTIN = !getFieldValue([
                              "taxDomiciles",
                              index,
                              "isTINIDAvailable",
                            ]);

                            if (noTIN) return null;

                            return (
                              <Form.Item
                                label="Please select a reason"
                                name={[field.name, "taxDomicileReasonID"]}
                                rules={[
                                  {
                                    required: true,
                                    message: "Please select reason",
                                  },
                                ]}
                              >
                                <Radio.Group>
                                  {tinReasonOptions.map((r) => (
                                    <Radio
                                      key={r.value}
                                      value={r.value}
                                      className="block mb-2"
                                    >
                                      {r.label}
                                    </Radio>
                                  ))}
                                </Radio.Group>
                              </Form.Item>
                            );
                          }}
                        </Form.Item>

                        <AlternateTaxIdentifierField
                          field={field}
                          index={index}
                          label="Business / License Number"
                        />
                      </div>
                    </div>
                  ))}

                  {hasOtherCrsTaxResident && (
                    <div className="mt-6">
                      <Button
                        type="dashed"
                        block
                        onClick={() =>
                          add({
                            taxDomicileCountryId: null,
                            taxIDNumber: "",
                            isTINIDAvailable: false,
                            taxDomicileReasonID: null,
                            alternateTaxIdentifier: "",
                          })
                        }
                      >
                        + Add Another Tax Resident Country
                      </Button>
                    </div>
                  )}
                </>
              )}
            </Form.List>
          </div>
        </div>
      </div>

      {/* Registered Office Address Section */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold mb-4">
          Registered Office Address
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Office Address */}
          <Form.Item
            label="Office Address"
            name="registeredOfficeAddress"
            rules={[{ required: true, message: "Please enter office address" }]}
          >
            <Input
              placeholder="Enter office address"
              onChange={handleRegisteredAddressChange}
            />
          </Form.Item>

          {/* Street Name */}
          <Form.Item
            label="Street Name"
            name="registeredStreetName"
            rules={[{ required: true, message: "Please enter street name" }]}
          >
            <Input
              placeholder="Enter street name"
              onChange={handleRegisteredAddressChange}
            />
          </Form.Item>

          {/* Country */}
          <Form.Item
            label="Country"
            name="registeredCountry"
            rules={[{ required: true, message: "Please select country" }]}
          >
            <Select
              showSearch
              placeholder="Select country"
              filterOption={filterOption}
              options={countries}
              onChange={handleRegisteredAddressChange}
            />
          </Form.Item>

          {/* City/Town */}
          <Form.Item
            label="City/Town"
            name="registeredCityTown"
            rules={[{ required: true, message: "Please enter city/town" }]}
          >
            <Input
              placeholder="Enter city/town"
              onChange={handleRegisteredAddressChange}
            />
          </Form.Item>

          {/* Postal/Zip Code */}
          <Form.Item
            label="Postal/Zip Code"
            name="registeredPostalZipCode"
            rules={[
              { required: true, message: "Please enter postal/zip code" },
            ]}
          >
            <Input
              placeholder="Enter postal/zip code"
              onChange={handleRegisteredAddressChange}
            />
          </Form.Item>
        </div>
      </div>
  
      {/* Correspondence Address Section */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Correspondence Address</h3>
          <Form.Item
            name="sameAsAbove"
            valuePropName="checked"
            className="mb-0"
          >
            <Checkbox onChange={handleSameAsAboveChange}>
              Same as Above
            </Checkbox>
          </Form.Item>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Office Address */}
          <Form.Item
            label="Office Address"
            name="correspondenceOfficeAddress"
            rules={[{ required: true, message: "Please enter office address" }]}
          >
            <Input
              placeholder="Enter office address"
              disabled={isDisabled || sameAsAbove}
            />
          </Form.Item>

          {/* Street Name */}
          <Form.Item
            label="Street Name"
            name="correspondenceStreetName"
            rules={[{ required: true, message: "Please enter street name" }]}
          >
            <Input
              placeholder="Enter street name"
              disabled={isDisabled || sameAsAbove}
            />
          </Form.Item>

          {/* Country */}
          <Form.Item
            label="Country"
            name="correspondenceCountry"
            rules={[{ required: true, message: "Please select country" }]}
          >
            <Select
              showSearch
              placeholder="Select country"
              filterOption={filterOption}
              options={countries}
              disabled={isDisabled || sameAsAbove}
            />
          </Form.Item>

          {/* City/Town */}
          <Form.Item
            label="City/Town"
            name="correspondenceCityTown"
            rules={[{ required: true, message: "Please enter city/town" }]}
          >
            <Input
              placeholder="Enter city/town"
              disabled={isDisabled || sameAsAbove}
            />
          </Form.Item>

          {/* Postal/Zip Code */}
          <Form.Item
            label="Postal/Zip Code"
            name="correspondencePostalZipCode"
            rules={[
              { required: true, message: "Please enter postal/zip code" },
            ]}
          >
            <Input
              placeholder="Enter postal/zip code"
              disabled={isDisabled || sameAsAbove}
            />
          </Form.Item>
        </div>
      </div>
    </>
  );
};

export default CorporateBeneficiaryForm;
